import { matchesPatientOrActivityTitleSearch } from "@/pages/commandcenter/Activities/ActivitiesTable/activitiesTableUtils";
import {
  classifyShiftItem,
  shiftItemKindSchema,
  type IShiftWithContents,
} from "@medoma/models/shiftWithContents";

/**
 * A shift paired with the result of applying the active filter to it.
 *
 * A matching shift always shows all of its content; `matchedItemIds` marks the
 * visits and standalone activities that matched the filter so the UI can
 * highlight them. `isFilterMatch` is true when the shift has at least one match
 * (and always true when no filter is active).
 */
export type ShiftWithFilteredContents = {
  shiftWithContents: IShiftWithContents;
  matchedItemIds: Set<string>;
  containsFilterMatch: boolean;
};

/** Ids of the visits and standalone activities matching the filter. */
const collectMatchedItemIds = (
  shift: IShiftWithContents,
  filter: string,
): Set<string> => {
  const ids = new Set<string>();
  for (const shiftItem of shift.items.map(classifyShiftItem)) {
    switch (shiftItem.kind) {
      case shiftItemKindSchema.enum.route:
        for (const visit of shiftItem.item.visits) {
          if (
            matchesPatientOrActivityTitleSearch(filter, visit.patient, visit)
          ) {
            ids.add(visit.id);
          }
        }
        break;
      case shiftItemKindSchema.enum.group:
      case shiftItemKindSchema.enum.occurrence:
        if (
          matchesPatientOrActivityTitleSearch(
            filter,
            shiftItem.item.patient,
            shiftItem.item,
          )
        ) {
          ids.add(shiftItem.item.id);
        }
        break;
      default:
        throw shiftItem satisfies never;
    }
  }
  return ids;
};

/** Builds the filter-aware view model for a single shift. */
export const toShiftWithFilteredContents = (
  shift: IShiftWithContents,
  filter: string,
): ShiftWithFilteredContents => {
  if (filter === "") {
    return {
      shiftWithContents: shift,
      matchedItemIds: new Set(),
      containsFilterMatch: true,
    };
  }

  const matchedItemIds = collectMatchedItemIds(shift, filter);
  return {
    shiftWithContents: shift,
    matchedItemIds,
    containsFilterMatch: matchedItemIds.size > 0,
  };
};
